import {
  angleDelta, canTarget, DIFFICULTY_CONFIG, FIXED_STEP,
  MAP_CONFIG, MAX_PROJECTILES, PROJECTILE_SPEED, PROJECTILE_TTL, SHIP_CONFIG, STEERING_CONFIG,
} from './config';
import type { BattleEvent, BattleOptions, BattleSnapshot, GameCommand, ProjectileState, ShipKind, ShipState, Side } from './types';

const TAU = Math.PI * 2;
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

/** First intersection along a swept segment, including a segment starting inside the circle. */
function sweepCircle(x: number, z: number, endX: number, endZ: number, cx: number, cz: number, radius: number): number | null {
  const dx = endX - x;
  const dz = endZ - z;
  const ox = x - cx;
  const oz = z - cz;
  const c = ox * ox + oz * oz - radius * radius;
  if (c <= 0) return 0;
  const a = dx * dx + dz * dz;
  if (a < 1e-12) return null;
  const b = 2 * (ox * dx + oz * dz);
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;
  const t = (-b - Math.sqrt(discriminant)) / (2 * a);
  return t >= 0 && t <= 1 ? t : null;
}

export class BattleEngine {
  private state: BattleSnapshot;
  private events: BattleEvent[] = [];
  private accumulator = 0;
  private steering = 0;
  private nextProjectileId = 1;
  private randomState: number;
  private disposed = false;

  constructor(options: BattleOptions) {
    this.randomState = (options.seed ?? Math.floor(Math.random() * 4294967296)) >>> 0;
    const seed = this.randomState;
    const map = MAP_CONFIG[options.map];
    this.state = {
      phase: 'playing', time: 0, options: { ...options, seed }, playerId: 1, ships: [], projectiles: [],
      islands: map.islands.map(island => ({ ...island })), arenaRadius: map.arenaRadius,
      wind: { heading: this.random() * TAU, strength: 0.5 + this.random() * 0.5 },
      kills: 0, shots: 0, hits: 0, score: 0,
    };
    this.state.ships.push(this.createShip(1, options.ship, 'player', 0, 0, 0));
    const count = DIFFICULTY_CONFIG[options.difficulty].enemyCount;
    for (let i = 0; i < count; i++) {
      const bearing = (i - (count - 1) / 2) * 0.8 + 0.12 + this.random() * 0.2;
      const distance = 70 + this.random() * 25;
      const kind: ShipKind = options.difficulty === 'easy' || i % 3 !== 2 ? 'sloop' : 'frigate';
      const ship = this.createShip(i + 2, kind, 'enemy', Math.sin(bearing) * distance, Math.cos(bearing) * distance, bearing + Math.PI);
      this.resolveWorldCollision(ship);
      this.state.ships.push(ship);
    }
  }

  command(command: GameCommand): void {
    if (this.disposed) return;
    if (command.type === 'pause') {
      if (this.state.phase === 'playing') this.state.phase = 'paused';
      this.steering = 0;
      this.accumulator = 0;
      return;
    }
    if (command.type === 'resume') {
      if (this.state.phase === 'paused') this.state.phase = 'playing';
      this.accumulator = 0;
      return;
    }
    if (this.state.phase !== 'playing') return;
    const player = this.state.ships[0];
    if (command.type === 'steer') this.steering = Number.isFinite(command.value) ? clamp(command.value, -1, 1) : 0;
    else if (command.type === 'throttle') player.throttle = Number.isFinite(command.value) ? clamp(command.value, 0, 1) : player.throttle;
    else if (command.type === 'fire') this.fire(player, command.side);
  }

  step(dt: number): void {
    if (this.disposed || this.state.phase !== 'playing' || !Number.isFinite(dt) || dt <= 0) return;
    // A delayed browser frame may advance at most 250 ms, never several hidden-tab seconds.
    this.accumulator += Math.min(dt, 0.25);
    while (this.accumulator + 1e-10 >= FIXED_STEP && this.state.phase === 'playing') {
      this.accumulator = Math.max(0, this.accumulator - FIXED_STEP);
      this.simulate(FIXED_STEP);
    }
  }

  getSnapshot(): BattleSnapshot {
    return {
      ...this.state, options: { ...this.state.options }, wind: { ...this.state.wind },
      ships: this.state.ships.map(ship => ({ ...ship })),
      projectiles: this.state.projectiles.map(projectile => ({ ...projectile })),
      islands: this.state.islands.map(island => ({ ...island })),
    };
  }

  drainEvents(): BattleEvent[] {
    return this.events.splice(0);
  }

  dispose(): void {
    this.disposed = true;
    this.steering = 0;
    this.accumulator = 0;
    this.state.projectiles = [];
    this.events = [];
  }

  private random(): number {
    this.randomState = (this.randomState + 0x6d2b79f5) >>> 0;
    let value = this.randomState;
    value = Math.imul(value ^ value >>> 15, value | 1);
    value ^= value + Math.imul(value ^ value >>> 7, value | 61);
    return ((value ^ value >>> 14) >>> 0) / 4294967296;
  }

  private createShip(id: number, kind: ShipKind, faction: 'player' | 'enemy', x: number, z: number, heading: number): ShipState {
    const maxHp = Math.round(SHIP_CONFIG[kind].maxHp * (faction === 'enemy' ? DIFFICULTY_CONFIG[this.state.options.difficulty].healthScale : 1));
    return {
      id, kind, faction, x, z, heading: angleDelta(heading, 0), speed: 0, throttle: faction === 'player' ? 0.55 : 0.7,
      hp: maxHp, maxHp, portCooldown: 0, starboardCooldown: 0, alive: true, sunkAt: null,
    };
  }

  private simulate(dt: number): void {
    this.state.time += dt;
    for (const ship of this.state.ships) {
      if (!ship.alive) continue;
      ship.portCooldown = Math.max(0, ship.portCooldown - dt);
      ship.starboardCooldown = Math.max(0, ship.starboardCooldown - dt);
      // Player input is captain-relative: positive is right rudder. AI returns world-yaw direction.
      const turn = ship.faction === 'player' ? -this.steering : this.enemySteering(ship);
      const config = SHIP_CONFIG[ship.kind];
      const handling = STEERING_CONFIG[ship.faction];
      const turnAuthority = handling.stationaryAuthority + (1 - handling.stationaryAuthority) * ship.speed / config.maxSpeed;
      // No angular momentum: a press, release or reversal applies on the next fixed simulation step.
      if (turn !== 0) ship.heading = angleDelta(ship.heading + turn * config.turnRate * handling.rateMultiplier * turnAuthority * dt, 0);
      const windBoost = 1 + 0.08 * this.state.wind.strength * Math.cos(ship.heading - this.state.wind.heading);
      const desiredSpeed = ship.throttle * config.maxSpeed * windBoost;
      const acceleration = 5;
      ship.speed += clamp(desiredSpeed - ship.speed, -acceleration * dt, acceleration * dt);
      ship.x += Math.sin(ship.heading) * ship.speed * dt;
      ship.z += Math.cos(ship.heading) * ship.speed * dt;
      this.resolveWorldCollision(ship);
      if (ship.faction === 'enemy') {
        const player = this.state.ships[0];
        if (canTarget(ship, player, 'port')) this.fire(ship, 'port');
        else if (canTarget(ship, player, 'starboard')) this.fire(ship, 'starboard');
      }
    }
    this.resolveShipCollisions();
    this.moveProjectiles(dt);
    this.finishIfNeeded();
  }

  private enemySteering(ship: ShipState): number {
    const player = this.state.ships[0];
    const x = player.x - ship.x;
    const z = player.z - ship.z;
    const distance = Math.hypot(x, z);
    const bearing = Math.atan2(x, z);
    const range = SHIP_CONFIG[ship.kind].range;
    const orbitSign = ship.id % 2 === 0 ? 1 : -1;
    let desired = bearing;
    if (distance < range * 0.82) {
      // A broadside pass, with a small inward component if drifting away from the target.
      desired = bearing + orbitSign * (Math.PI / 2 - clamp((distance - range * 0.58) / range, -0.4, 0.3));
      ship.throttle = distance < 20 ? 0.8 : 0.58;
    } else ship.throttle = 0.85;
    let directionX = Math.sin(desired);
    let directionZ = Math.cos(desired);
    for (const island of this.state.islands) {
      const awayX = ship.x - island.x;
      const awayZ = ship.z - island.z;
      const separation = Math.hypot(awayX, awayZ);
      const safeDistance = island.radius + SHIP_CONFIG[ship.kind].radius + 22;
      if (separation < safeDistance && separation > 0.001) {
        const strength = 3 * (1 - separation / safeDistance);
        directionX += awayX / separation * strength;
        directionZ += awayZ / separation * strength;
      }
    }
    const worldDistance = Math.hypot(ship.x, ship.z);
    if (worldDistance > this.state.arenaRadius - 30) {
      directionX -= ship.x / worldDistance * 3;
      directionZ -= ship.z / worldDistance * 3;
    }
    desired = Math.atan2(directionX, directionZ);
    return clamp(angleDelta(desired, ship.heading) * 2.2, -1, 1);
  }

  private resolveWorldCollision(ship: ShipState): void {
    const radius = SHIP_CONFIG[ship.kind].radius;
    for (const island of this.state.islands) {
      const dx = ship.x - island.x;
      const dz = ship.z - island.z;
      const distance = Math.hypot(dx, dz);
      const safeDistance = radius + island.radius;
      if (distance < safeDistance) {
        const nx = distance > 0.001 ? dx / distance : 1;
        const nz = distance > 0.001 ? dz / distance : 0;
        ship.x = island.x + nx * safeDistance;
        ship.z = island.z + nz * safeDistance;
        ship.speed *= 0.35;
      }
    }
    const distance = Math.hypot(ship.x, ship.z);
    const boundary = this.state.arenaRadius - radius;
    if (distance > boundary) {
      ship.x *= boundary / distance;
      ship.z *= boundary / distance;
      ship.speed *= 0.35;
    }
  }

  private resolveShipCollisions(): void {
    const ships = this.state.ships;
    for (let i = 0; i < ships.length; i++) {
      for (let j = i + 1; j < ships.length; j++) {
        const a = ships[i];
        const b = ships[j];
        if (!a.alive || !b.alive) continue;
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const distance = Math.hypot(dx, dz);
        const radii = SHIP_CONFIG[a.kind].radius + SHIP_CONFIG[b.kind].radius;
        if (distance >= radii) continue;
        const nx = distance > 0.001 ? dx / distance : 1;
        const nz = distance > 0.001 ? dz / distance : 0;
        const shift = (radii - distance) / 2;
        a.x -= nx * shift; a.z -= nz * shift;
        b.x += nx * shift; b.z += nz * shift;
        a.speed *= 0.9; b.speed *= 0.9;
        this.resolveWorldCollision(a);
        this.resolveWorldCollision(b);
      }
    }
  }

  private fire(ship: ShipState, side: Side): void {
    if (!ship.alive) return;
    const cooldownKey = side === 'port' ? 'portCooldown' : 'starboardCooldown';
    const config = SHIP_CONFIG[ship.kind];
    if (ship[cooldownKey] > 1e-8 || this.state.projectiles.length + config.cannons > MAX_PROJECTILES) return;
    const difficulty = DIFFICULTY_CONFIG[this.state.options.difficulty];
    ship[cooldownKey] = config.reload * (ship.faction === 'enemy' ? difficulty.reloadScale : 1);
    const sideSign = side === 'starboard' ? -1 : 1;
    const broadside = ship.heading + sideSign * Math.PI / 2;
    const assistAngle = difficulty.aimAssistAngle;
    const target = assistAngle > 0 ? this.state.ships.filter(other => canTarget(ship, other, side) &&
      Math.abs(angleDelta(Math.atan2(other.x - ship.x, other.z - ship.z), broadside)) <= assistAngle)
      .sort((a, b) => Math.hypot(a.x - ship.x, a.z - ship.z) - Math.hypot(b.x - ship.x, b.z - ship.z))[0]
      : undefined;
    let aimX = target?.x ?? ship.x + Math.sin(broadside) * config.range;
    let aimZ = target?.z ?? ship.z + Math.cos(broadside) * config.range;
    if (target) {
      const flightTime = Math.min(1.6, Math.hypot(aimX - ship.x, aimZ - ship.z) / PROJECTILE_SPEED);
      aimX += Math.sin(target.heading) * target.speed * flightTime;
      aimZ += Math.cos(target.heading) * target.speed * flightTime;
    }
    const eventHeading = target ? broadside + clamp(angleDelta(Math.atan2(aimX - ship.x, aimZ - ship.z), broadside), -assistAngle, assistAngle) : broadside;
    this.events.push({ type: 'fire', shipId: ship.id, side, x: ship.x, z: ship.z, heading: eventHeading, faction: ship.faction });
    for (let i = 0; i < config.cannons; i++) {
      const alongHull = (i / (config.cannons - 1) - 0.5) * config.length * 0.65;
      const x = ship.x + Math.sin(ship.heading) * alongHull + Math.cos(ship.heading) * sideSign * (config.radius + 0.3);
      const z = ship.z + Math.cos(ship.heading) * alongHull - Math.sin(ship.heading) * sideSign * (config.radius + 0.3);
      const bearing = target ? Math.atan2(aimX - x, aimZ - z) : broadside;
      const spread = ship.faction === 'enemy' ? difficulty.aimSpread : 0.013;
      const heading = broadside + (target ? clamp(angleDelta(bearing, broadside), -assistAngle, assistAngle) : 0) + (this.random() - 0.5) * spread;
      this.state.projectiles.push({ id: this.nextProjectileId++, ownerId: ship.id, faction: ship.faction, x, z, vx: Math.sin(heading) * PROJECTILE_SPEED, vz: Math.cos(heading) * PROJECTILE_SPEED, age: 0, ttl: PROJECTILE_TTL });
    }
    if (ship.faction === 'player') this.state.shots += config.cannons;
  }

  private moveProjectiles(dt: number): void {
    const active: ProjectileState[] = [];
    for (const projectile of this.state.projectiles) {
      const owner = this.state.ships.find(ship => ship.id === projectile.ownerId)!;
      const duration = Math.min(projectile.ttl, SHIP_CONFIG[owner.kind].range / PROJECTILE_SPEED);
      const remaining = duration - projectile.age;
      if (remaining <= 1e-10) continue;
      const travelTime = Math.min(dt, remaining);
      const endX = projectile.x + projectile.vx * travelTime;
      const endZ = projectile.z + projectile.vz * travelTime;
      let closest = Infinity;
      let hitShip: ShipState | null = null;
      for (const island of this.state.islands) {
        const t = sweepCircle(projectile.x, projectile.z, endX, endZ, island.x, island.z, island.radius + 0.4);
        if (t !== null && t < closest) { closest = t; hitShip = null; }
      }
      for (const ship of this.state.ships) {
        if (!ship.alive || ship.faction === projectile.faction || ship.id === projectile.ownerId) continue;
        const t = sweepCircle(projectile.x, projectile.z, endX, endZ, ship.x, ship.z, SHIP_CONFIG[ship.kind].radius + 0.4);
        if (t !== null && t < closest) { closest = t; hitShip = ship; }
      }
      if (closest !== Infinity) {
        const impactX = projectile.x + (endX - projectile.x) * closest;
        const impactZ = projectile.z + (endZ - projectile.z) * closest;
        if (hitShip) {
          const damage = SHIP_CONFIG[owner.kind].damage * (owner.faction === 'enemy' ? DIFFICULTY_CONFIG[this.state.options.difficulty].damageScale : 1);
          hitShip.hp = Math.max(0, hitShip.hp - damage);
          this.events.push({ type: 'hit', shipId: hitShip.id, x: impactX, z: impactZ, damage, faction: hitShip.faction });
          if (projectile.faction === 'player') this.state.hits++;
          if (hitShip.hp === 0) this.sink(hitShip);
        }
        continue;
      }
      projectile.x = endX; projectile.z = endZ; projectile.age += travelTime;
      if (projectile.age + 1e-10 < duration) active.push(projectile);
    }
    this.state.projectiles = active;
  }

  private sink(ship: ShipState): void {
    ship.alive = false; ship.sunkAt = this.state.time; ship.speed = 0; ship.throttle = 0;
    ship.portCooldown = 0; ship.starboardCooldown = 0;
    if (ship.faction === 'enemy') this.state.kills++;
    this.events.push({ type: 'sink', shipId: ship.id, x: ship.x, z: ship.z, faction: ship.faction });
  }

  private finishIfNeeded(): void {
    const player = this.state.ships[0];
    const outcome = !player.alive ? 'defeat' : this.state.ships.every(ship => ship.faction === 'player' || !ship.alive) ? 'victory' : null;
    if (!outcome) return;
    this.state.phase = outcome;
    this.steering = 0;
    this.accumulator = 0;
    const accuracy = this.state.shots > 0 ? this.state.hits / this.state.shots : 0;
    this.state.score = Math.max(0, Math.round(this.state.kills * 1000 + player.hp * 5 + accuracy * 300 - this.state.time * 3));
    this.events.push({ type: 'result', outcome, score: this.state.score });
  }
}
